import React, { useState, useEffect, useCallback } from 'react';

const TournamentSettings = ({ tournamentId = 1 }) => {
  const [tournament, setTournament] = useState(null);
  const [event, setEvent] = useState(null);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [eventName, setEventName] = useState('');
  const [gameType, setGameType] = useState('');
  const [buyInAmount, setBuyInAmount] = useState('');
  const [rakeAmount, setRakeAmount] = useState('');
  const [useFixedRake, setUseFixedRake] = useState(false);
  const [fixedRakeTotal, setFixedRakeTotal] = useState('');
  const [startingChips, setStartingChips] = useState('');
  const [maxPlayers, setMaxPlayers] = useState('');
  const [selectedStructure, setSelectedStructure] = useState('');
  const [registrationOpens, setRegistrationOpens] = useState('');
  const [registrationCloses, setRegistrationCloses] = useState('');
  const [eventDate, setEventDate] = useState('');

  const getApiUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000';
    } else if (hostname === '10.0.0.28') {
      return `https://${hostname}/api`;
    } else {
      return `https://${hostname}/api`;
    }
  };

  // Convert UTC datetime to local datetime-local format
const utcToLocal = (utcDateString) => {
  if (!utcDateString) return '';
  console.log('Input UTC string:', utcDateString);
  const date = new Date(utcDateString);
  console.log('Date object:', date);
  console.log('Local time:', date.toLocaleString());
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const result = `${year}-${month}-${day}T${hours}:${minutes}`;
  console.log('Output local string:', result);
  return result;
};

// Convert local datetime-local format to ISO string (UTC)
const localToUTC = (localDateString) => {
  if (!localDateString) return null;
  return new Date(localDateString).toISOString();
};

  const loadTournamentData = useCallback(async () => {
    try {
      console.log('Loading tournament data for ID:', tournamentId);
      
      // Load tournament
      const tournamentResponse = await fetch(`${getApiUrl()}/tournaments/${tournamentId}`);
      if (!tournamentResponse.ok) {
        throw new Error(`Tournament not found: ${tournamentResponse.status}`);
      }
      const tournamentData = await tournamentResponse.json();
      console.log('Tournament loaded:', tournamentData);
      setTournament(tournamentData);

      // Load associated event
      const eventResponse = await fetch(`${getApiUrl()}/events/${tournamentData.event_id}`);
      if (!eventResponse.ok) {
        throw new Error(`Event not found: ${eventResponse.status}`);
      }
      const eventData = await eventResponse.json();
      console.log('Event loaded:', eventData);
      setEvent(eventData);

      // Populate form fields only after both are loaded
      setEventName(eventData.name || '');
      setGameType(eventData.game_type || '');
      setBuyInAmount(eventData.buy_in_amount || '');
      setRakeAmount(eventData.rake_amount || '');
      setUseFixedRake(eventData.use_fixed_rake || false);
      setFixedRakeTotal(eventData.fixed_rake_total || '');
      setStartingChips(eventData.starting_chips || '');
      setMaxPlayers(eventData.max_players || '');
      setSelectedStructure(tournamentData.structure_id || '');
      setRegistrationOpens(utcToLocal(eventData.registration_opens));
      setRegistrationCloses(utcToLocal(eventData.registration_closes));
      setEventDate(eventData.event_date || '');

      setLoading(false);
    } catch (error) {
      console.error('Error loading tournament data:', error);
      setLoading(false);
    }
  }, [tournamentId]);

  const loadBlindStructures = useCallback(async () => {
    try {
      const response = await fetch(`${getApiUrl()}/blind-structures`);
      const data = await response.json();
      setStructures(data);
    } catch (error) {
      console.error('Error loading structures:', error);
    }
  }, []);

  const updateEvent = async () => {
    if (!event) {
      console.error('No event data available');
      alert('No event data available');
      return;
    }

    try {
      // Calculate prize pool per player (for per-player rake mode)
      const prizePoolAmount = parseFloat(buyInAmount) - parseFloat(rakeAmount || 0);
      
      const eventData = {
        name: eventName,
        game_type: gameType,
        event_date: eventDate, // ADD THIS LINE
        buy_in_amount: parseFloat(buyInAmount),
        rake_amount: parseFloat(rakeAmount || 0),
        use_fixed_rake: useFixedRake,
        fixed_rake_total: useFixedRake ? parseFloat(fixedRakeTotal || 0) : null,
        prize_pool_amount: prizePoolAmount,
        starting_chips: parseInt(startingChips),
        max_players: maxPlayers ? parseInt(maxPlayers) : null,
        registration_opens: localToUTC(registrationOpens),
        registration_closes: localToUTC(registrationCloses)
      };

      console.log('Sending event update:', eventData);

      const response = await fetch(`${getApiUrl()}/events/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(eventData)
      });

      if (response.ok) {
        alert('Event updated successfully!');
        loadTournamentData();
      } else {
        const errorData = await response.json();
        console.error('Update failed:', errorData);
        alert(`Error updating event: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Error updating event: ' + error.message);
    }
  };

  const updateTournamentStructure = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          structure_id: parseInt(selectedStructure)
        })
      });

      if (response.ok) {
        alert('Tournament structure updated successfully!');
        loadTournamentData();
      } else {
        const errorData = await response.json();
        alert(`Error updating structure: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating tournament structure:', error);
      alert('Error updating tournament structure: ' + error.message);
    }
  };

  const generateNewDealerCode = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/generate-dealer-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(`New dealer code generated: ${data.dealer_code}`);
        loadTournamentData();
      }
    } catch (error) {
      console.error('Error generating dealer code:', error);
    }
  };

  useEffect(() => {
    loadTournamentData();
    loadBlindStructures();
  }, [loadTournamentData, loadBlindStructures]);

  if (loading) {
    return <div className="container">Loading tournament settings...</div>;
  }

  if (!tournament || !event) {
    return <div className="container">Tournament or event data not found...</div>;
  }

  // Calculate displayed prize pool based on rake type
  const calculatePrizePool = () => {
    const buyIn = parseFloat(buyInAmount || 0);
    if (useFixedRake) {
      const fixedRake = parseFloat(fixedRakeTotal || 0);
      return `Buy-in per player: $${buyIn.toFixed(2)} | Total Fixed Rake: $${fixedRake.toFixed(2)}`;
    } else {
      const rake = parseFloat(rakeAmount || 0);
      const perPlayer = buyIn - rake;
      return `$${perPlayer.toFixed(2)} per player (Buy-in: $${buyIn.toFixed(2)} - Rake: $${rake.toFixed(2)})`;
    }
  };

  return (
    <div className="container" style={{ maxWidth: '1200px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Tournament Settings</h1>
        <button 
          className="btn"
          onClick={() => window.location.href = `/admin/tournament/${tournamentId}/control`}
        >
          ← Back to Control Panel
        </button>
      </div>
      
      {/* Tournament Info */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>Tournament Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div><strong>Tournament ID:</strong> {tournament.id}</div>
          <div><strong>Status:</strong> {tournament.status}</div>
          <div><strong>Current Level:</strong> {tournament.current_level}</div>
          <div><strong>Players Remaining:</strong> {tournament.players_remaining}</div>
          <div><strong>Dealer Code:</strong> {tournament.dealer_code}</div>
          <div><strong>Created:</strong> {new Date(tournament.created_at).toLocaleString()}</div>
        </div>
        <div style={{ marginTop: '15px' }}>
          <button className="btn" onClick={generateNewDealerCode}>
            Generate New Dealer Code
          </button>
        </div>
      </div>

      {/* Event Settings */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>Event Settings</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
          <div className="form-group">
            <label>Event Name:</label>
            <input 
              type="text"
              className="form-control"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Event Date:</label>
            <input 
              type="date"
              className="form-control"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label>Game Type:</label>
            <select 
              className="form-control"
              value={gameType}
              onChange={(e) => setGameType(e.target.value)}
            >
              <option value="NLH">No Limit Hold'em</option>
              <option value="PLO">Pot Limit Omaha</option>
              <option value="NLO">No Limit Omaha</option>
              <option value="Teams">Teams Event</option>
              <option value="Bounty">Bounty Event</option>
            </select>
          </div>

          <div className="form-group">
            <label>Buy-in Amount:</label>
            <input 
              type="number"
              step="0.01"
              className="form-control"
              value={buyInAmount}
              onChange={(e) => setBuyInAmount(e.target.value)}
            />
          </div>

          {/* Rake Type Toggle */}
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>
              <input 
                type="checkbox"
                checked={useFixedRake}
                onChange={(e) => setUseFixedRake(e.target.checked)}
              />
              {' '}Use Fixed Total Rake (instead of per-player rake)
            </label>
          </div>

          {!useFixedRake ? (
            <div className="form-group">
              <label>Rake/Admin Fee (per player):</label>
              <input 
                type="number"
                step="0.01"
                className="form-control"
                value={rakeAmount}
                onChange={(e) => setRakeAmount(e.target.value)}
              />
            </div>
          ) : (
            <div className="form-group">
              <label>Fixed Total Rake (for entire tournament):</label>
              <input 
                type="number"
                step="0.01"
                className="form-control"
                value={fixedRakeTotal}
                onChange={(e) => setFixedRakeTotal(e.target.value)}
                placeholder="e.g., 50.00"
              />
            </div>
          )}

          <div className="form-group">
            <label>Starting Chips:</label>
            <input 
              type="number"
              className="form-control"
              value={startingChips}
              onChange={(e) => setStartingChips(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Max Players:</label>
            <input 
              type="number"
              className="form-control"
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(e.target.value)}
              placeholder="Leave empty for unlimited"
            />
          </div>

          <div className="form-group">
            <label>Registration Opens:</label>
            <input 
              type="datetime-local"
              className="form-control"
              value={registrationOpens}
              onChange={(e) => setRegistrationOpens(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Registration Closes:</label>
            <input 
              type="datetime-local"
              className="form-control"
              value={registrationCloses}
              onChange={(e) => setRegistrationCloses(e.target.value)}
            />
          </div>
        </div>

        <div style={{ margin: '20px 0', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <strong>Prize Pool Calculation:</strong><br/>
          {calculatePrizePool()}
        </div>

        <button className="btn btn-success" onClick={updateEvent}>
          Update Event Settings
        </button>
      </div>

      {/* Blind Structure */}
      <div className="card">
        <h3>Blind Structure</h3>
        <div className="form-group">
          <label>Select Structure:</label>
          <select 
            className="form-control"
            value={selectedStructure}
            onChange={(e) => setSelectedStructure(e.target.value)}
          >
            <option value="">Select a blind structure</option>
            {structures.map(structure => (
              <option key={structure.id} value={structure.id}>
                {structure.name} ({structure.structure_json.length} levels)
              </option>
            ))}
          </select>
        </div>

        <button className="btn btn-success" onClick={updateTournamentStructure}>
          Update Tournament Structure
        </button>
      </div>
    </div>
  );
};

export default TournamentSettings;